import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { cities } from 'turkey-map-react/lib/data';
import { findRegionByCity, getResponsibleManagerIds } from './regionUtils';
import useCenterText from './centerText';
import { RegionDot } from './parts/centerBits';

const UNASSIGNED_LIGHT = '#e2e8f0';
const UNASSIGNED_DARK = '#475569';
const TOOLTIP_OFFSET = 14;
const TOOLTIP_WIDTH = 288;

/**
 * Province map of Türkiye coloured by region (Tailwind). A click adds the
 * province to / removes it from the selected region; hovering shows its
 * region, managers and center / worker / project counts. The tooltip is
 * portalled to <body> so the dialog's transform cannot shift it.
 */
const TurkeyRegionMap = ({
  regions,
  selectedRegionId,
  onCityClick,
  employeesMap,
  isDarkMode,
  getCityDetails
}) => {
  const { label } = useCenterText();
  const [tooltip, setTooltip] = useState(null);

  const getManagerNames = (region, cityName) => {
    if (!region) return [];
    return getResponsibleManagerIds(region, cityName)
      .map((managerId) => employeesMap[managerId])
      .filter(Boolean);
  };

  const handleMouseMove = (event, city) => {
    const region = findRegionByCity(regions, city.name);
    const details = getCityDetails ? getCityDetails(city.name) : null;

    setTooltip({
      x: event.clientX,
      y: event.clientY,
      cityName: city.name,
      regionName: region?.name || null,
      regionColor: region?.color || null,
      managerNames: getManagerNames(region, city.name),
      details
    });
  };

  // Keeps the tooltip inside the window near the right / bottom edges
  const tooltipPosition = (point) => {
    const left = point.x + TOOLTIP_OFFSET + TOOLTIP_WIDTH > window.innerWidth
      ? Math.max(8, point.x - TOOLTIP_OFFSET - TOOLTIP_WIDTH)
      : point.x + TOOLTIP_OFFSET;
    const top = point.y + TOOLTIP_OFFSET + 160 > window.innerHeight
      ? Math.max(8, point.y - TOOLTIP_OFFSET - 160)
      : point.y + TOOLTIP_OFFSET;
    return { left, top };
  };

  return (
    <div className="tw-relative tw-rounded-3xl tw-border tw-border-slate-200/80 tw-bg-gradient-to-b tw-from-sky-50/80 tw-to-white tw-p-2 sm:tw-p-4 dark:tw-border-white/10 dark:tw-from-white/[0.04] dark:tw-to-white/[0.01]">
      <svg
        viewBox="12 134 1027 464"
        className="tw-block tw-h-auto tw-max-h-[360px] tw-w-full md:tw-max-h-[520px]"
        role="img"
        aria-label={label('regions.map.label')}
      >
        {cities.map((city) => {
          const region = findRegionByCity(regions, city.name);
          const isInSelectedRegion = region && region.id === selectedRegionId;
          const fill = region
            ? region.color
            : (isDarkMode ? UNASSIGNED_DARK : UNASSIGNED_LIGHT);
          // With a region selected, provinces of the other regions step back a little
          const dimmed = selectedRegionId && region && !isInSelectedRegion;

          return (
            <path
              key={city.plateNumber}
              d={city.path}
              fill={fill}
              className={`tw-cursor-pointer tw-stroke-white tw-transition-[filter,opacity] tw-duration-150 hover:tw-brightness-[0.85] dark:tw-stroke-[#1f232b] ${isInSelectedRegion ? 'tw-stroke-[1.5] tw-drop-shadow-[0_0_2px_rgba(15,23,42,0.4)]' : 'tw-stroke-1'} ${dimmed ? 'tw-opacity-70' : ''}`}
              onClick={() => onCityClick(city)}
              onMouseMove={(event) => handleMouseMove(event, city)}
              onMouseLeave={() => setTooltip(null)}
            />
          );
        })}
      </svg>

      {tooltip && createPortal(
        <div
          role="tooltip"
          style={tooltipPosition(tooltip)}
          className="tw-pointer-events-none tw-fixed tw-z-[1200] tw-w-max tw-max-w-[18rem] tw-space-y-1 tw-rounded-xl tw-border tw-border-white/10 tw-bg-slate-900/95 tw-px-3 tw-py-2.5 tw-font-sans tw-text-xs tw-text-white tw-shadow-xl tw-backdrop-blur-md"
        >
          <div className="tw-text-sm tw-font-semibold">{tooltip.cityName}</div>
          {tooltip.regionName ? (
            <>
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-font-medium tw-text-slate-200">
                <RegionDot color={tooltip.regionColor} className="tw-h-2 tw-w-2" />
                {tooltip.regionName}
              </div>
              {tooltip.managerNames.length > 0 && (
                <div className="tw-text-slate-300">
                  {label('regions.map.manager', { names: tooltip.managerNames.join(', ') })}
                </div>
              )}
            </>
          ) : (
            <div className="tw-italic tw-text-slate-400">{label('regions.map.unassigned')}</div>
          )}

          {tooltip.details && tooltip.details.centerCount > 0 && (
            <div className="tw-mt-1.5 tw-space-y-0.5 tw-border-t tw-border-white/10 tw-pt-1.5 tw-text-slate-300">
              <div className="tw-font-semibold tw-text-white">
                {label('regions.map.summary', { centers: tooltip.details.centerCount, workers: tooltip.details.workerCount, projects: tooltip.details.projectCount })}
              </div>
              {tooltip.details.centers.slice(0, 4).map((center) => (
                <div key={center.id} className="tw-leading-snug">
                  • {label('regions.map.centerLine', { name: center.name, workers: center.workerCount, projects: center.projectCount })}
                </div>
              ))}
              {tooltip.details.centers.length > 4 && (
                <div>{label('regions.map.more', { count: tooltip.details.centers.length - 4 })}</div>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

TurkeyRegionMap.propTypes = {
  regions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedRegionId: PropTypes.string,
  onCityClick: PropTypes.func.isRequired,
  employeesMap: PropTypes.object.isRequired,
  isDarkMode: PropTypes.bool,
  getCityDetails: PropTypes.func,
};

export default TurkeyRegionMap;
